import React, { FC, useCallback, useState } from 'react';
import { Box, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Button, ButtonColor } from 'components/Button/Button';
import { Dialog } from 'components/Dialog/Dialog';
import { Footer } from 'components/Footer/Footer';
import { Icon } from 'components/Icon/Icon';
import { Text, TextTypography } from 'components/Text/Text';

import { CroppedArea } from 'types';

import { ReactComponent as ImageIcon } from 'assets/icons/additional/image-edit.svg';

import { CoverImageControlContents } from './CoverImageControlContents';
import { CoverImageControlEmpty } from './CoverImageControlEmpty';
import { CropperMode } from './types';
import i18n from '../../../i18n';

export interface ImageControlProps {
    mode: CropperMode;
    onChange: () => void;
    cropSize: {
        width: number;
        height: number;
    };
    cropAspect: number;
    displayedValue: any;
    onClose: () => void;
    onDelete: () => void;
    onOpenFileDialog: () => void;
    onChangeCrop: (value: CroppedArea) => void;
    previewElement: Element;
    hideRemoveButton?: boolean;
}

const useStyles = makeStyles({
    dialogContent: {
        padding: '0 !important',
    },
    dialogInner: {
        width: '100%',
    },
    footerButtons: {
        minWidth: 250,
        textAlign: 'right',
    },
    upload: {
        cursor: 'pointer',
    },
    uploadIcon: {
        position: 'relative',
        top: 2,
        marginRight: 4,
    },
    cancelButton: {
        marginRight: 16,
        height: 48,
    },
    okButton: {
        height: 48,
    },
});

export const CoverImageControlPopup: FC<ImageControlProps> = (props: ImageControlProps) => {
    const {
        mode,
        cropSize,
        cropAspect,
        displayedValue,
        onChangeCrop,
        onClose,
        onDelete,
        onOpenFileDialog,
        onChange,
        previewElement,
        hideRemoveButton,
    } = props;

    const [croppedValue, setCroppedValue] = useState<CroppedArea>(null);
    const { t } = i18n.useTranslation();

    const handleCropComplete = useCallback(
        (data: CroppedArea) => {
            setCroppedValue(data);
        },
        [setCroppedValue],
    );

    const save = useCallback(() => {
        if (displayedValue && !displayedValue.startsWith('blob')) {
            onClose();
            return;
        }
        onChange();
        onChangeCrop(croppedValue);
        onClose();
    }, [displayedValue, croppedValue, onChangeCrop, onClose]);

    const classes = useStyles();
    return (
        <Dialog hideCloseButton={true} contentStyles={classes.dialogContent} onClose={onClose} width={cropSize.width}>
            <div className={classes.dialogInner}>
                {displayedValue ? (
                    <CoverImageControlContents
                        mode={mode}
                        value={displayedValue}
                        cropSize={cropSize}
                        cropAspect={cropAspect}
                        onDelete={onDelete}
                        onCropComplete={handleCropComplete}
                        previewElement={previewElement}
                        hideRemoveButton={hideRemoveButton}
                    />
                ) : (
                    <div style={{ height: cropSize.height + 20 }} onClick={onOpenFileDialog}>
                        <CoverImageControlEmpty label={'Image'} />
                    </div>
                )}
                <Footer>
                    <Box m={4} mb={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={6} style={{ minWidth: 240 }}>
                                <Text
                                    className={classes.upload}
                                    typography={TextTypography.PARA_ACCENT}
                                    onClick={onOpenFileDialog}
                                >
                                    <Icon className={classes.uploadIcon} component={ImageIcon} svgSize={16} size={16} />
                                    {t('common:imageControl.upload')}
                                </Text>
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} className={classes.footerButtons}>
                                <Button color={ButtonColor.GRAY} onClick={onClose} className={classes.cancelButton}>
                                    {t('common:imageControl.cancel')}
                                </Button>
                                <Button color={ButtonColor.DARK} onClick={save} className={classes.okButton}>
                                    {t('common:imageControl.save')}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Footer>
            </div>
        </Dialog>
    );
};
